/**
 * useAuthSession — autenticazione Phase 5 (IMPL-P5-005).
 *
 * Context + Provider + hook for the current auth session. The Provider
 * fetches `/api/auth/custom/me` on mount, exposes `{ user, status, refresh,
 * signOut }`, and is the single source of truth the rest of the SPA uses
 * (e.g. `RequireAuth`, role-aware layouts).
 *
 * Status semantics:
 *  - `loading`         — initial mount, /me still in flight
 *  - `authenticated`   — /me returned a user
 *  - `unauthenticated` — /me returned 401 / null user / errored
 *
 * The Provider is intentionally TSX (not .ts) because it returns JSX.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { authClient, type SessionUser } from '../lib/auth-client';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  user: SessionUser | null;
  status: AuthStatus;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthSessionContext = createContext<AuthState | null>(null);

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  const refresh = useCallback(async () => {
    try {
      const res = await authClient.getMe();
      if (res.user) {
        setUser(res.user);
        setStatus('authenticated');
      } else {
        setUser(null);
        setStatus('unauthenticated');
      }
    } catch {
      // Network or unexpected status: collapse to unauthenticated rather than
      // throw — the SPA stays usable and the user can retry by navigating.
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const signOut = useCallback(async () => {
    // Best-effort: even if the network call fails, we always reset client
    // state so the UI stops rendering authenticated content.
    await authClient.logout().catch(() => {
      /* fall-through */
    });
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <AuthSessionContext.Provider value={{ user, status, refresh, signOut }}>
      {children}
    </AuthSessionContext.Provider>
  );
}

export function useAuthSession(): AuthState {
  const ctx = useContext(AuthSessionContext);
  if (!ctx) {
    throw new Error('useAuthSession must be used inside <AuthSessionProvider>');
  }
  return ctx;
}
